import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Info,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  RefreshCw,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import {
  getPortalPbCustomerJson,
  getPortalPbToken,
  getPortalPocketBaseUrl,
  PORTAL_DEMO_ACCESS_URL,
} from '../../../constants';
import { PORTAL_SHELL } from '../portalTheme';
import { seatCeiling } from '../orbisSeatLimits';

export interface KanzleiZugaengeViewProps {
  kanzlei: string;
  kanzleiId?: string;
  customerStatus?: string;
  /** Portal-tier: `standard` = Orbis Professional, `premium` = Orbis Premium. */
  tier?: 'standard' | 'premium';
  authToken: string | null;
  includedSeats?: number | null;
  maxSeats?: number | null;
}

type TeamMember = {
  id: string;
  email: string;
  name: string;
  portal_role?: string;
  is_primary?: boolean;
  must_change_password?: boolean;
};

function randomTempPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint8Array(14);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += alphabet[bytes[i]! % alphabet.length];
  }
  return out;
}

function randomIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
}

function formatDemoAccessError(
  status: number,
  data: { code?: string; message?: string; retryAfterSeconds?: number },
): string {
  if (status === 401) return 'Sitzung abgelaufen — bitte erneut anmelden.';
  if (status === 403) return 'Nur der berechtigte Firma-Demo-Admin darf Zugänge senden.';
  if (status === 429) {
    const minutes =
      typeof data.retryAfterSeconds === 'number' && data.retryAfterSeconds > 0
        ? ` (${Math.ceil(data.retryAfterSeconds / 60)} Min.)`
        : '';
    return `Versand-Limit erreicht — bitte später erneut versuchen${minutes}.`;
  }
  if (data.code === 'MAIL_SEND_FAILED') {
    return 'Zugang angelegt, E-Mail aber nicht versendet. Erneut senden stellt ein neues temporäres Passwort aus.';
  }
  return data.message?.trim() || `Demo-Zugang konnte nicht gesendet werden (HTTP ${status}).`;
}

/** PocketBase Create-Fehler → klare DE-Meldung (nicht nur „Failed to create record.“). */
function formatInviteCreateError(
  status: number,
  data: { message?: string; data?: Record<string, unknown> },
): string {
  const fields = data.data && typeof data.data === 'object' ? data.data : {};
  const emailErr = fields.email as { code?: string; message?: string } | undefined;
  if (emailErr?.code === 'validation_not_unique') {
    return 'Diese E-Mail ist bereits vergeben — bitte eine andere Adresse wählen.';
  }
  if (emailErr?.code === 'validation_invalid_email' || emailErr?.code === 'validation_required') {
    return 'Ungültige oder fehlende E-Mail-Adresse.';
  }
  const nameErr = fields.name as { code?: string } | undefined;
  if (nameErr?.code === 'validation_required') {
    return 'Name ist Pflicht.';
  }
  const passwordErr = (fields.password || fields.passwordConfirm) as { code?: string } | undefined;
  if (passwordErr?.code) {
    return 'Passwort ungültig — bitte erneut versuchen.';
  }
  const msg = typeof data.message === 'string' ? data.message.trim() : '';
  if (msg && !/^failed to create record\.?$/i.test(msg)) {
    return msg;
  }
  // Feld-Hinweise sammeln falls PB nur Generic-Message liefert
  const fieldHints: string[] = [];
  for (const [key, val] of Object.entries(fields)) {
    if (val && typeof val === 'object' && 'message' in val) {
      const m = String((val as { message?: string }).message || '').trim();
      if (m) fieldHints.push(`${key}: ${m}`);
    }
  }
  if (fieldHints.length) {
    return `Eingabe ungültig — ${fieldHints.join('; ')}`;
  }
  return `Einladung fehlgeschlagen (HTTP ${status}).`;
}

function formatManageError(
  status: number,
  data: { message?: string; data?: Record<string, unknown> },
): string {
  const fields = data.data && typeof data.data === 'object' ? data.data : {};
  const emailErr = fields.email as { code?: string; message?: string } | undefined;
  if (emailErr?.code === 'validation_not_unique') {
    return 'Diese E-Mail ist bereits vergeben — bitte eine andere Adresse wählen.';
  }
  if (
    emailErr?.code === 'validation_invalid_email' ||
    emailErr?.code === 'validation_required' ||
    emailErr?.code === 'validation_values_mismatch'
  ) {
    return 'Ungültige oder fehlende E-Mail-Adresse.';
  }
  const nameErr = fields.name as { code?: string } | undefined;
  if (nameErr?.code === 'validation_required') {
    return 'Name ist Pflicht.';
  }
  const passwordErr = (fields.password || fields.passwordConfirm) as { code?: string } | undefined;
  if (passwordErr?.code) {
    return 'Passwort ungültig — bitte erneut versuchen.';
  }
  if (status === 401) return 'Sitzung abgelaufen — bitte erneut anmelden.';
  if (status === 403) {
    const msg = typeof data.message === 'string' ? data.message.trim() : '';
    return msg || 'Keine Berechtigung für diese Aktion.';
  }
  const msg = typeof data.message === 'string' ? data.message.trim() : '';
  if (msg && !/^failed to (update|delete) record\.?$/i.test(msg)) {
    return msg;
  }
  return `Aktion fehlgeschlagen (HTTP ${status}).`;
}

function currentPortalUserId(): string {
  try {
    const raw = getPortalPbCustomerJson();
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { id?: string };
    return typeof parsed.id === 'string' ? parsed.id : '';
  } catch {
    return '';
  }
}

type MemberDialog =
  | { kind: 'edit'; member: TeamMember }
  | { kind: 'password'; member: TeamMember }
  | { kind: 'delete'; member: TeamMember };

export const KanzleiZugaengeView: React.FC<KanzleiZugaengeViewProps> = ({
  kanzlei,
  kanzleiId,
  customerStatus,
  tier = 'standard',
  authToken,
  includedSeats,
  maxSeats,
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteHint, setInviteHint] = useState('');
  const [lastTempPassword, setLastTempPassword] = useState('');
  const [dialog, setDialog] = useState<MemberDialog | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [actingId, setActingId] = useState('');
  const selfId = currentPortalUserId();
  const loadSeqRef = React.useRef(0);
  const hasLoadedOnceRef = React.useRef(false);
  const authTokenRef = React.useRef(authToken);
  const demoRequestRef = React.useRef<{ fingerprint: string; key: string } | null>(null);
  authTokenRef.current = authToken;
  const demoMailEnabled = customerStatus === 'FIRMA_DEMO';

  const ceiling = useMemo(
    () => seatCeiling(includedSeats, maxSeats),
    [includedSeats, maxSeats],
  );
  const used = members.length;
  const atLimit = ceiling != null && used >= ceiling;
  const inviteDisabledBySeatLimit = atLimit && !demoMailEnabled;

  const resolveToken = useCallback((): string | null => {
    const fromProp =
      typeof authTokenRef.current === 'string' ? authTokenRef.current.trim() : '';
    if (fromProp) return fromProp;
    // Session-first (Multi-Account): sessionStorage, ggf. remember→local Hydration.
    // Nie nur localStorage — sonst „Keine Session“ bei remember=false.
    return getPortalPbToken();
  }, []);

  const loadMembers = useCallback(async (opts?: { forceSpinner?: boolean }) => {
    const seq = ++loadSeqRef.current;
    setError('');
    const token = resolveToken();
    if (!token) {
      if (seq === loadSeqRef.current) {
        setError(
          'Keine gültige Portal-Session (Token fehlt) — bitte erneut anmelden. Bei „Angemeldet bleiben“ aus: Session gilt nur in diesem Tab.',
        );
        setLoading(false);
      }
      return;
    }
    if (!kanzleiId) {
      if (seq === loadSeqRef.current) {
        setError('kanzlei_id fehlt — Org nicht provisioniert (Slice A).');
        setLoading(false);
      }
      return;
    }
    // Nur Erstload / manueller Refresh: Spinner — sonst Zucken bei Parent-Re-Renders.
    const showSpinner = opts?.forceSpinner === true || !hasLoadedOnceRef.current;
    if (showSpinner && seq === loadSeqRef.current) setLoading(true);
    try {
      const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
      // Kein sort=created: PB-Labor Auth-Collection + Team-listRule → HTTP 400
      // „Something went wrong while processing your request.“ (UAT 07.08.2026).
      const qs = new URLSearchParams({
        filter: `kanzlei_id="${kanzleiId.replace(/"/g, '')}"`,
        perPage: '100',
        sort: 'email',
      });
      const res = await fetch(`${pbBase}/api/collections/customers/records?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (await res.json().catch(() => ({}))) as {
        items?: Array<Record<string, unknown>>;
        message?: string;
      };
      if (!res.ok) {
        throw new Error(
          typeof data.message === 'string' && data.message
            ? data.message
            : `Mitgliederliste fehlgeschlagen (HTTP ${res.status})`,
        );
      }
      // email kann bei Auth-Collection + emailVisibility=false für Team-Peers fehlen —
      // Sitze trotzdem zählen (UAT: nur Admin sichtbar → „1 von 5“ trotz Member).
      const items = (data.items || []).map((r) => ({
        id: String(r.id || ''),
        email: typeof r.email === 'string' ? r.email : '',
        name: String(r.name || ''),
        portal_role: typeof r.portal_role === 'string' ? r.portal_role : undefined,
        is_primary: r.is_primary === true,
        must_change_password: r.must_change_password === true,
      }));
      if (seq === loadSeqRef.current) {
        setMembers(items.filter((m) => m.id));
        hasLoadedOnceRef.current = true;
      }
    } catch (err) {
      if (seq === loadSeqRef.current) {
        // Liste nicht leeren bei Refresh-Fehler — verhindert Zucken „0 Sitze“ + Banner-Loop.
        setError(err instanceof Error ? err.message : 'Mitgliederliste fehlgeschlagen');
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [kanzleiId, resolveToken]);

  // Nur bei kanzleiId-Wechsel / Mount — nicht bei Token-/Parent-Re-Renders.
  useEffect(() => {
    hasLoadedOnceRef.current = false;
    void loadMembers({ forceSpinner: true });
  }, [loadMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteHint('');
    setLastTempPassword('');
    setError('');
    const token = resolveToken();
    if (!token) {
      setError(
        'Einladung nicht möglich — Portal-Token fehlt. Bitte neu anmelden (Session liegt in sessionStorage, nicht nur localStorage).',
      );
      return;
    }
    if (!kanzleiId) {
      setError('Einladung nicht möglich — kanzlei_id fehlt (Org nicht provisioniert).');
      return;
    }
    if (inviteDisabledBySeatLimit) {
      setError('Sitz-Limit erreicht. Weitere Lizenzen: Nachkauf auf Anfrage.');
      return;
    }
    const email = inviteEmail.trim().toLowerCase();
    const name = inviteName.trim();
    if (!email || !name) {
      setError('E-Mail und Name sind Pflicht.');
      return;
    }
    setInviting(true);
    try {
      if (demoMailEnabled) {
        const fingerprint = `${name}\n${email}`;
        if (!demoRequestRef.current || demoRequestRef.current.fingerprint !== fingerprint) {
          demoRequestRef.current = { fingerprint, key: randomIdempotencyKey() };
        }
        const res = await fetch(PORTAL_DEMO_ACCESS_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Idempotency-Key': demoRequestRef.current.key,
          },
          body: JSON.stringify({ name, email }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          code?: string;
          message?: string;
          retryAfterSeconds?: number;
        };
        if (!res.ok || data.ok !== true) {
          throw new Error(formatDemoAccessError(res.status, data));
        }
        demoRequestRef.current = null;
        setInviteHint(
          data.code === 'ALREADY_SENT'
            ? 'Dieser Versand wurde bereits verarbeitet — es wurde kein zweiter Zugang angelegt.'
            : 'Demo-Zugang wurde per E-Mail versendet. Das temporäre Passwort erscheint nur in der Mail.',
        );
        setInviteEmail('');
        setInviteName('');
        await loadMembers({ forceSpinner: false });
        return;
      }

      const tempPw = randomTempPassword();
      const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
      // Server-Hook setzt kanzlei_id / Rolle / must_change_password — Client schickt keine Org-ID.
      const res = await fetch(`${pbBase}/api/collections/customers/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          password: tempPw,
          passwordConfirm: tempPw,
          name,
          // Platzhalter — Hook überschreibt Org/Rolle/must_change_password/Sitze/tier
          kanzlei: kanzlei || '—',
          tier,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        message?: string;
        data?: Record<string, unknown>;
      };
      if (!res.ok) {
        throw new Error(formatInviteCreateError(res.status, data));
      }
      setLastTempPassword(tempPw);
      setInviteHint(
        `Zugang angelegt für ${email}. Erstpasswort unten — Nutzer muss es beim Login ändern.`,
      );
      setInviteEmail('');
      setInviteName('');
      await loadMembers({ forceSpinner: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Einladung fehlgeschlagen');
    } finally {
      setInviting(false);
    }
  };

  const memberRecordUrl = (id: string) => {
    const pbBase = getPortalPocketBaseUrl().replace(/\/$/, '');
    return `${pbBase}/api/collections/customers/records/${encodeURIComponent(id)}`;
  };

  const openEdit = (member: TeamMember) => {
    setError('');
    setInviteHint('');
    setEditName(member.name);
    setEditEmail(member.email);
    setDialog({ kind: 'edit', member });
  };

  const handleSaveEdit = async () => {
    if (!dialog || dialog.kind !== 'edit') return;
    const token = resolveToken();
    if (!token) {
      setError('Keine gültige Portal-Session — bitte erneut anmelden.');
      return;
    }
    const name = editName.trim();
    const email = editEmail.trim().toLowerCase();
    if (!name || !email) {
      setError('E-Mail und Name sind Pflicht.');
      return;
    }
    setActingId(dialog.member.id);
    setError('');
    try {
      const res = await fetch(memberRecordUrl(dialog.member.id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, email, emailConfirm: email }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        message?: string;
        data?: Record<string, unknown>;
      };
      if (!res.ok) {
        throw new Error(formatManageError(res.status, data));
      }
      setDialog(null);
      setInviteHint(`Zugang aktualisiert: ${name} · ${email}`);
      await loadMembers({ forceSpinner: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktualisierung fehlgeschlagen');
    } finally {
      setActingId('');
    }
  };

  const handlePasswordReset = async () => {
    if (!dialog || dialog.kind !== 'password') return;
    const token = resolveToken();
    if (!token) {
      setError('Keine gültige Portal-Session — bitte erneut anmelden.');
      return;
    }
    const tempPw = randomTempPassword();
    setActingId(dialog.member.id);
    setError('');
    setLastTempPassword('');
    try {
      const res = await fetch(memberRecordUrl(dialog.member.id), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          password: tempPw,
          passwordConfirm: tempPw,
          must_change_password: true,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        message?: string;
        data?: Record<string, unknown>;
        must_change_password?: boolean;
      };
      if (!res.ok) {
        throw new Error(formatManageError(res.status, data));
      }
      setDialog(null);
      setLastTempPassword(tempPw);
      setInviteHint(
        `Neues Passwort für ${dialog.member.email || dialog.member.name} gesetzt. Nutzer muss es beim Login ändern.`,
      );
      await loadMembers({ forceSpinner: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passwort-Reset fehlgeschlagen');
    } finally {
      setActingId('');
    }
  };

  const handleDeleteMember = async () => {
    if (!dialog || dialog.kind !== 'delete') return;
    const token = resolveToken();
    if (!token) {
      setError('Keine gültige Portal-Session — bitte erneut anmelden.');
      return;
    }
    setActingId(dialog.member.id);
    setError('');
    try {
      const res = await fetch(memberRecordUrl(dialog.member.id), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          message?: string;
          data?: Record<string, unknown>;
        };
        throw new Error(formatManageError(res.status, data));
      }
      setDialog(null);
      setInviteHint(
        `Zugang gelöscht — Sitz ist frei (${Math.max(0, used - 1)}${ceiling != null ? ` von ${ceiling}` : ''}).`,
      );
      await loadMembers({ forceSpinner: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen fehlgeschlagen');
    } finally {
      setActingId('');
    }
  };

  const seatHint =
    ceiling != null
      ? `${used} von ${ceiling} Sitzen belegt` +
        (typeof includedSeats === 'number' && includedSeats > 0
          ? ` · ${includedSeats} inkludiert laut Vertrag`
          : '')
      : 'Sitz-Limit noch nicht an der Kanzlei hinterlegt';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-white tracking-tight">Kanzlei &amp; Zugänge</h1>
        <p className="text-slate-400 text-sm mt-1">
          Team-Zugänge für <span className="text-slate-300">{kanzlei}</span> verwalten
        </p>
      </div>

      <section className={`${PORTAL_SHELL.ingestBar} p-5 space-y-4`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-blue-400" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-white font-medium text-sm">Aktive Zugänge</p>
              <p className="text-slate-500 text-xs mt-0.5">{seatHint}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void loadMembers({ forceSpinner: true })}
            disabled={loading}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40"
            title="Aktualisieren"
            aria-label="Mitgliederliste aktualisieren"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
          </button>
        </div>

        {ceiling != null && (
          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden" aria-hidden>
            <div
              className={`h-full rounded-full transition-all ${
                atLimit ? 'bg-amber-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(100, Math.round((used / ceiling) * 100))}%` }}
            />
          </div>
        )}

        {atLimit && (
          <div className="flex gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100/95 text-xs leading-relaxed">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400" aria-hidden />
            {demoMailEnabled ? (
              <p>
                Demo-Kontingent erreicht ({used}/{ceiling}). Neue Adressen werden abgelehnt;
                ein bestehender Demo-Zugang kann erneut ausgestellt werden.
              </p>
            ) : (
              <p>
                Sitz-Limit erreicht ({used}/{ceiling}). Weitere Nutzerlizenzen:{' '}
                <span className="font-semibold">Nachkauf auf Anfrage</span> bei ARKORBIT — kein
                Self-Service-Billing im Portal.
              </p>
            )}
          </div>
        )}

        {loading && members.length === 0 ? (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            Lade Team…
          </div>
        ) : members.length === 0 && !error ? (
          <div className="rounded-xl border border-dashed border-white/[0.08] bg-slate-900/50 px-4 py-6 text-center">
            <Mail className="w-8 h-8 text-slate-600 mx-auto mb-3" aria-hidden />
            <p className="text-slate-400 text-sm">Noch keine Team-Mitglieder geladen.</p>
          </div>
        ) : members.length > 0 ? (
          <ul className="divide-y divide-white/[0.06] rounded-xl border border-white/[0.06] overflow-hidden">
            {members.map((m) => {
              const isSelf = Boolean(selfId) && m.id === selfId;
              const canDelete = !m.is_primary && !isSelf;
              const busy = actingId === m.id;
              return (
                <li
                  key={m.id}
                  className="flex flex-col gap-2 px-4 py-3 bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">
                      {m.name || m.email || 'Zugang ohne Anzeigename'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {m.email || 'E-Mail ausgeblendet (Sichtbarkeit)'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[11px] uppercase tracking-wide text-slate-400">
                        {m.is_primary
                          ? 'Primär'
                          : m.portal_role === 'it_admin'
                            ? 'IT-Admin'
                            : 'Member'}
                      </span>
                      {m.must_change_password && (
                        <span className="text-[10px] text-amber-400/90">PW-Wechsel offen</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        disabled={busy}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40"
                        title="Name oder E-Mail ändern"
                        aria-label={`Zugang ${m.name || m.email} bearbeiten`}
                      >
                        <Pencil className="w-3.5 h-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setError('');
                          setInviteHint('');
                          setDialog({ kind: 'password', member: m });
                        }}
                        disabled={busy || isSelf}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] disabled:opacity-40"
                        title={
                          isSelf
                            ? 'Eigenes Passwort über Einstellungen ändern'
                            : 'Neues temporäres Passwort setzen'
                        }
                        aria-label={`Passwort für ${m.name || m.email} zurücksetzen`}
                      >
                        <KeyRound className="w-3.5 h-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setError('');
                          setInviteHint('');
                          setDialog({ kind: 'delete', member: m });
                        }}
                        disabled={busy || !canDelete}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent"
                        title={
                          m.is_primary
                            ? 'Primär-Account kann nicht gelöscht werden'
                            : isSelf
                              ? 'Eigene Sitzung kann nicht gelöscht werden'
                              : 'Zugang löschen — Sitz wird frei'
                        }
                        aria-label={`Zugang ${m.name || m.email} löschen`}
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}

        <form onSubmit={handleInvite} className="space-y-3 pt-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-xs text-slate-400">Name</span>
              <input
                type="text"
                value={inviteName}
                onChange={(ev) => setInviteName(ev.target.value)}
                disabled={inviting || inviteDisabledBySeatLimit}
                className="w-full rounded-xl bg-slate-950/80 border border-white/[0.08] px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/40 disabled:opacity-50"
                placeholder="Vor- und Nachname"
                autoComplete="off"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs text-slate-400">E-Mail</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(ev) => setInviteEmail(ev.target.value)}
                disabled={inviting || inviteDisabledBySeatLimit}
                className="w-full rounded-xl bg-slate-950/80 border border-white/[0.08] px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/40 disabled:opacity-50"
                placeholder="nutzer@kanzlei.example"
                autoComplete="off"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={inviting || inviteDisabledBySeatLimit || !kanzleiId || !resolveToken()}
            title={
              inviteDisabledBySeatLimit
                ? 'Sitz-Limit erreicht — Nachkauf auf Anfrage'
                : demoMailEnabled
                  ? 'Temporären Firma-Demo-Zugang per E-Mail senden'
                  : 'Nutzer mit Pflicht-Passwortwechsel anlegen'
            }
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600/90 hover:bg-blue-500 disabled:bg-slate-800 disabled:border disabled:border-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
          >
            {inviting ? (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            ) : demoMailEnabled ? (
              <Mail className="w-4 h-4" aria-hidden />
            ) : (
              <UserPlus className="w-4 h-4" aria-hidden />
            )}
            {inviteDisabledBySeatLimit
              ? 'Limit erreicht — Nachkauf auf Anfrage'
              : demoMailEnabled
                ? 'Demo-Zugang senden'
                : 'Nutzer einladen'}
          </button>
        </form>

        {inviteHint && (
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3 text-emerald-100/90 text-xs space-y-2">
            <p>{inviteHint}</p>
            {lastTempPassword && (
              <p className="font-mono text-emerald-50/95 break-all">
                Temporäres Passwort: {lastTempPassword}
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="flex gap-2.5 rounded-xl border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-rose-100/90 text-xs leading-relaxed">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-rose-400" aria-hidden />
            <p>{error}</p>
          </div>
        )}

        <div className="flex gap-2.5 rounded-xl border border-white/[0.06] bg-slate-900/40 px-4 py-3 text-slate-400 text-xs leading-relaxed">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-500" aria-hidden />
          {demoMailEnabled ? (
            <p>
              Der Server legt ausschließlich einen{' '}
              <span className="text-slate-300">FIRMA_DEMO</span>-Member im gemeinsamen
              Demo-Tenant an. Das temporäre Passwort wird nur per Brevo-Mail zugestellt und muss
              beim ersten Login geändert werden. Mitglieder sehen diese Ansicht nicht.
            </p>
          ) : (
            <p>
              Einladungen setzen serverseitig{' '}
              <span className="text-slate-300">portal_role=member</span>, dieselbe{' '}
              <span className="text-slate-300">kanzlei_id</span> und{' '}
              <span className="text-slate-300">must_change_password</span>. IT-Admin kann Name,
              E-Mail und Passwort ändern oder den Zugang löschen (Sitz wird frei). Primär-Account
              und der letzte IT-Admin sind geschützt. Mitglieder sehen diese Ansicht nicht.
              AuthZ bleibt Header-User-ID — Org mappt der Server.
            </p>
          )}
        </div>
      </section>

      {dialog && (
        <div
          className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-[2px] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="seats-member-dialog-title"
        >
          <div className="w-full max-w-md rounded-xl border border-white/[0.08] bg-slate-950 shadow-2xl p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <h2 id="seats-member-dialog-title" className="text-sm font-semibold text-white">
                {dialog.kind === 'edit' && 'Zugang bearbeiten'}
                {dialog.kind === 'password' && 'Neues Passwort setzen'}
                {dialog.kind === 'delete' && 'Zugang löschen'}
              </h2>
              <button
                type="button"
                onClick={() => setDialog(null)}
                disabled={Boolean(actingId)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06]"
                aria-label="Dialog schließen"
              >
                <X className="w-4 h-4" aria-hidden />
              </button>
            </div>

            {dialog.kind === 'edit' && (
              <div className="space-y-3">
                <label className="block space-y-1.5">
                  <span className="text-xs text-slate-400">Name</span>
                  <input
                    type="text"
                    value={editName}
                    onChange={(ev) => setEditName(ev.target.value)}
                    disabled={Boolean(actingId)}
                    className="w-full rounded-xl bg-slate-950/80 border border-white/[0.08] px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/40 disabled:opacity-50"
                    autoComplete="off"
                  />
                </label>
                <label className="block space-y-1.5">
                  <span className="text-xs text-slate-400">E-Mail</span>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(ev) => setEditEmail(ev.target.value)}
                    disabled={Boolean(actingId)}
                    className="w-full rounded-xl bg-slate-950/80 border border-white/[0.08] px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/40 disabled:opacity-50"
                    autoComplete="off"
                  />
                </label>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setDialog(null)}
                    disabled={Boolean(actingId)}
                    className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-slate-300 text-sm"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSaveEdit()}
                    disabled={Boolean(actingId)}
                    className="flex-1 py-2.5 rounded-xl bg-blue-600/90 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm"
                  >
                    {actingId ? 'Speichern…' : 'Speichern'}
                  </button>
                </div>
              </div>
            )}

            {dialog.kind === 'password' && (
              <div className="space-y-3">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Für{' '}
                  <span className="text-slate-200">
                    {dialog.member.name || dialog.member.email}
                  </span>{' '}
                  wird ein temporäres Passwort erzeugt. Der Nutzer muss es beim nächsten Login
                  ändern.
                </p>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setDialog(null)}
                    disabled={Boolean(actingId)}
                    className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-slate-300 text-sm"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={() => void handlePasswordReset()}
                    disabled={Boolean(actingId)}
                    className="flex-1 py-2.5 rounded-xl bg-blue-600/90 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold text-sm"
                  >
                    {actingId ? 'Setzen…' : 'Passwort setzen'}
                  </button>
                </div>
              </div>
            )}

            {dialog.kind === 'delete' && (
              <div className="space-y-3">
                <p className="text-xs text-slate-400 leading-relaxed">
                  Zugang{' '}
                  <span className="text-slate-200">
                    {dialog.member.name || dialog.member.email}
                  </span>{' '}
                  wirklich löschen? Der Sitz wird frei und kann neu vergeben werden. Diese Aktion
                  kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setDialog(null)}
                    disabled={Boolean(actingId)}
                    className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-slate-300 text-sm"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDeleteMember()}
                    disabled={Boolean(actingId)}
                    className="flex-1 py-2.5 rounded-xl bg-rose-600/90 hover:bg-rose-500 disabled:opacity-50 text-white font-semibold text-sm"
                  >
                    {actingId ? 'Löschen…' : 'Endgültig löschen'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
